import { useState, useEffect } from 'react';
import { Truck, Clock, MapPin, Package, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { formatPrice } from '@/lib/utils';
import { CartItem } from '@/types';
import { StoreInfo } from '@/types/store';
import { getShippingOptions, ShippingOption } from '@/lib/shipbubble';

interface ShippingOptionsProps {
  storeInfo: StoreInfo;
  cartItems: CartItem[];
  destination: {
    address: string;
    city: string;
    state: string;
    country: string;
  };
  manualShippingCost: number;
  selectedOption: string;
  onOptionSelect: (optionId: string) => void;
  loading?: boolean;
}

export function ShippingOptions({
  storeInfo,
  cartItems,
  destination,
  manualShippingCost,
  selectedOption,
  onOptionSelect,
  loading = false
}: ShippingOptionsProps) {
  const [options, setOptions] = useState<ShippingOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadShippingOptions();
  }, [storeInfo, cartItems, destination, manualShippingCost]);

  const loadShippingOptions = async () => {
    if (!destination.address || !destination.city || !destination.state || !destination.country) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const result = await getShippingOptions(
        storeInfo,
        cartItems,
        destination,
        manualShippingCost
      );

      setOptions(result.options);
      
      // Auto-select the first option if none is selected
      if (!selectedOption && result.options.length > 0) {
        onOptionSelect(result.defaultOption);
      }
    } catch (err) {
      console.error('Failed to load shipping options:', err);
      setError('Failed to load shipping options');
    } finally {
      setIsLoading(false);
    }
  };

  const getOptionIcon = (option: ShippingOption) => {
    if (option.id === 'shipbubble') {
      return <Truck className="h-5 w-5 text-blue-600" />;
    }
    return <Package className="h-5 w-5 text-gray-600" />;
  };

  const getOptionBadge = (option: ShippingOption) => {
    if (option.id === 'shipbubble') {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400">
          <Truck className="h-3 w-3 mr-1" />
          Shipbubble
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300">
        Standard
      </span>
    );
  };

  if (isLoading) {
    return (
      <div className="space-y-3">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white">Shipping Options</h3>
        <div className="space-y-3">
          {[1, 2].map((i) => (
            <div key={i} className="animate-pulse">
              <div className="bg-gray-200 dark:bg-gray-700 rounded-lg p-4 h-20"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-3">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white">Shipping Options</h3>
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-600 dark:text-red-400 text-sm">{error}</p>
          <Button
            variant="outline"
            size="sm"
            onClick={loadShippingOptions}
            className="mt-2"
          >
            Retry
          </Button>
        </div>
      </div>
    );
  }

  if (options.length === 0) {
    return (
      <div className="space-y-3">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white">Shipping Options</h3>
        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
          <p className="text-gray-600 dark:text-gray-400 text-sm">
            No shipping options available. Please check your delivery address.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white">Shipping Options</h3>
      <div className="space-y-3">
        {options.map((option) => (
          <div
            key={option.id}
            className={`relative border rounded-lg p-4 cursor-pointer transition-all duration-200 ${
              selectedOption === option.id
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 shadow-md'
                : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
            }`}
            onClick={() => onOptionSelect(option.id)}
          >
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-3 flex-1">
                <div className="flex-shrink-0 mt-1">
                  {getOptionIcon(option)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2 mb-1">
                    <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                      {option.name}
                    </h4>
                    {getOptionBadge(option)}
                  </div>
                  
                  <div className="space-y-1">
                    <div className="flex items-center text-xs text-gray-500 dark:text-gray-400">
                      <Clock className="h-3 w-3 mr-1" />
                      {option.estimated_delivery}
                    </div>
                    
                    {option.tracking_available && (
                      <div className="flex items-center text-xs text-green-600 dark:text-green-400">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Tracking available
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="flex-shrink-0 text-right">
                <div className="text-lg font-semibold text-gray-900 dark:text-white">
                  {formatPrice(option.cost)}
                </div>
                {selectedOption === option.id && (
                  <div className="mt-1">
                    <CheckCircle className="h-4 w-4 text-blue-600" />
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
